// Feature Documentation: docs/features/nma-coding-review-approval.md
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from './AppContext';
import HeaderMenus from './HeaderMenus';
import { FeatureFlagsProvider } from './FeatureFlags';
import { callApi } from '../callApi';
import {
  Box,
  Container,
  Flex,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Badge,
  Spinner,
  Text,
  useToast,
  Card,
  CardBody
} from '@chakra-ui/react';

function ReviewDashboard() {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [daysBack, setDaysBack] = useState(30);
  const { accessToken } = useContext(AppContext);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    const fetchQueue = async () => {
      setLoading(true);
      try {
        const response = await callApi(`/api/review/queue?days_back=${daysBack}`, accessToken, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        const data = await response.json();
        if (data.queue) {
          setQueue(data.queue);
        }
      } catch (error) {
        console.error('Error fetching review queue:', error);
        toast({
          title: 'Error loading queue',
          description: 'Could not load the review queue. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    if (accessToken) {
      fetchQueue();
    }
  }, [accessToken, daysBack, toast]);

  const handleReviewClick = (internalCaseId) => {
    navigate(`/review/${internalCaseId}`);
  };

  if (loading) {
    return (
      <Container maxW="container.xl" py={8} centerContent>
        <Spinner size="xl" />
        <Text mt={4}>Loading review queue...</Text>
      </Container>
    );
  }

  return (
    <Box>
      <HeaderMenus />
      <Container maxW="container.xl" py={8}>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading>Coding Review Queue</Heading>
        <Flex align="center" gap={3}>
          <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">Timeframe:</Text>
          <Box w="150px">
            <select 
              value={daysBack} 
              onChange={(e) => setDaysBack(Number(e.target.value))}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '6px',
                border: '1px solid #E2E8F0',
                backgroundColor: 'white'
              }}
            >
              <option value={7}>Last 7 Days</option>
              <option value={14}>Last 14 Days</option>
              <option value={30}>Last 30 Days</option>
              <option value={60}>Last 60 Days</option>
              <option value={90}>Last 90 Days</option>
              <option value={365}>Last Year</option>
            </select>
          </Box>
        </Flex>
      </Flex>
      
      <Card>
        <CardBody>
          {queue.length === 0 ? (
            <Text>No cases currently need review.</Text>
          ) : (
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Case ID</Th>
                  <Th>Patient Name</Th>
                  <Th>Procedure Date</Th>
                  <Th>Items to Review</Th>
                  <Th>Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {queue.map((item) => (
                  <Tr key={item.internal_case_id}>
                    <Td>{item.external_case_id}</Td>
                    <Td>{item.patient_name}</Td>
                    <Td>{item.procedure_date ? new Date(item.procedure_date).toLocaleDateString() : 'N/A'}</Td>
                    <Td>
                      <Badge colorScheme="red" fontSize="0.9em" p={1} borderRadius="md">
                        {item.preliminary_count} preliminary codes
                      </Badge>
                    </Td>
                    <Td>
                      <Button 
                        colorScheme="blue" 
                        size="sm"
                        onClick={() => handleReviewClick(item.external_case_id)}
                      >
                        Review Case
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </Container>
    </Box>
  );
}

export default ReviewDashboard;
